import { ThemeType, useTheme } from '@wui/common-library'
import { defineStore } from 'pinia'
import { computed, onScopeDispose, ref, type Ref } from 'vue'

import { useLocalization } from '@/shared/composables/localization'

const COLOR_THEME_KEY = 'color-theme'
const BOOT_DARK_CLASS = 'theme-boot-dark'
const SYSTEM_THEME = 'system'
const LIGHT_THEME = 'light-1'
const DARK_THEME = 'lsDark-1'

export type ColorThemeChoice = typeof SYSTEM_THEME | typeof LIGHT_THEME | typeof DARK_THEME

const isColorThemeChoice = (value: string): value is ColorThemeChoice => {
  return value === SYSTEM_THEME || value === LIGHT_THEME || value === DARK_THEME
}

const getOsPreferredTheme = (): ThemeType => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return LIGHT_THEME
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? DARK_THEME : LIGHT_THEME
}

const resolveAppliedTheme = (choice: ColorThemeChoice): ThemeType => {
  return choice === SYSTEM_THEME ? getOsPreferredTheme() : choice
}

const parseStoredTheme = (raw: string): ColorThemeChoice => {
  let value = raw
  try {
    const parsed = JSON.parse(raw)
    if (typeof parsed === 'string') {
      value = parsed
    }
  } catch {
    // stored as a plain string
  }

  if (value === 'dark-1') {
    return DARK_THEME
  }
  if (isColorThemeChoice(value)) {
    return value
  }
  return SYSTEM_THEME
}

/** Убирает boot-класс из index.html — иначе фон остаётся тёмным после перехода на light. */
export const clearBootThemeClass = () => {
  document.documentElement.classList.remove(BOOT_DARK_CLASS)
}

type AvailableColorThemes = {
  label: string
  value: ColorThemeChoice
}

export const useColorTheme = defineStore('color-theme', () => {
  const { t } = useLocalization()

  const getColorThemeChoiceFromLocalStorage = (): ColorThemeChoice => {
    const stored = localStorage.getItem(COLOR_THEME_KEY)
    if (!stored) {
      return SYSTEM_THEME
    }
    return parseStoredTheme(stored)
  }

  const colorThemeChoice: Ref<ColorThemeChoice> = ref(getColorThemeChoiceFromLocalStorage())
  const localStorageColorTheme: Ref<ThemeType> = ref(resolveAppliedTheme(colorThemeChoice.value))

  const AVAILABLE_COLOR_THEMES: Ref<AvailableColorThemes[]> = computed(() => [
    {
      label: t('themes.light1'),
      value: LIGHT_THEME,
    },
    {
      label: t('themes.dark1'),
      value: DARK_THEME,
    },
    {
      label: t('themes.system'),
      value: SYSTEM_THEME,
    },
  ])

  let osPreferenceMediaQuery: MediaQueryList | null = null

  const applyTheme = async (theme: ThemeType) => {
    localStorageColorTheme.value = theme
    await useTheme(theme)
    clearBootThemeClass()
  }

  const persistChoice = () => {
    localStorage.setItem(COLOR_THEME_KEY, JSON.stringify(colorThemeChoice.value))
  }

  const onOsPreferenceChange = () => {
    if (colorThemeChoice.value !== SYSTEM_THEME) {
      return
    }
    void applyTheme(resolveAppliedTheme(SYSTEM_THEME))
  }

  const stopOsPreferenceListener = () => {
    osPreferenceMediaQuery?.removeEventListener('change', onOsPreferenceChange)
    osPreferenceMediaQuery = null
  }

  const startOsPreferenceListener = () => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return
    }
    stopOsPreferenceListener()
    osPreferenceMediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    osPreferenceMediaQuery.addEventListener('change', onOsPreferenceChange)
  }

  const setColorTheme = async (color: ColorThemeChoice) => {
    colorThemeChoice.value = color
    if (color === SYSTEM_THEME) {
      startOsPreferenceListener()
    } else {
      stopOsPreferenceListener()
    }
    persistChoice()
    await applyTheme(resolveAppliedTheme(color))
  }

  if (colorThemeChoice.value === SYSTEM_THEME) {
    startOsPreferenceListener()
  }

  onScopeDispose(() => {
    stopOsPreferenceListener()
  })

  return {
    localStorageColorTheme,
    colorThemeChoice,
    setColorTheme,
    AVAILABLE_COLOR_THEMES,
  }
})
